import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { AppShell } from "@/components/layout/app-shell";
import { resolveUrl } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCustomer } from "@/hooks/use-customer";
import { format } from "date-fns";
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Loader2, FileText, ClipboardList, CheckCircle2, TrendingUp, Zap, Coins, Flame, Plus, Trash2, Download, Upload, FileSpreadsheet, Laptop
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

export default function EnergyManagementPlanPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeCustomerId } = useCustomer();
  const proposalId = parseInt(id || "0");

  const [isOpen, setIsOpen] = useState(false);
  const [editingAnalysis, setEditingAnalysis] = useState<any | null>(null);
  const [selectedTask, setSelectedTask] = useState<string>("");
  
  const [name, setName] = useState("");
  const [ecmType, setEcmType] = useState("boiler");
  const [description, setDescription] = useState("");
  const [projectCost, setProjectCost] = useState("0");
  const [incentive, setIncentive] = useState("0");
  const [electricSavingsKwh, setElectricSavingsKwh] = useState("0");
  const [demandSavingsKw, setDemandSavingsKw] = useState("0");
  const [fossilFuelSavingsMmbtu, setFossilFuelSavingsMmbtu] = useState("0");
  const [fossilFuelSource, setFossilFuelSource] = useState("Natural Gas");
  const [energyCostSavings, setEnergyCostSavings] = useState("0");
  const [operationalSavings, setOperationalSavings] = useState("0");
  const [isEcm, setIsEcm] = useState(true);
  const [linkToDeve, setLinkToDeve] = useState(false);
  const [improvementType, setImprovementType] = useState("ECM");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  // Fetch proposal details
  const { data: proposal, isLoading: proposalLoading } = useQuery<any>({
    queryKey: [`/api/osem/${proposalId}`],
    enabled: !!proposalId
  });

  // Fetch facility analyses
  const { data: analysesData, isLoading: analysesLoading } = useQuery<any>({
    queryKey: [`/api/osem/${proposalId}/analyses`],
    enabled: !!proposalId
  });

  // Seed metrics to NYSERDA
  const seedNyserdaMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/nyserda/seed", { buildingId: activeCustomerId });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "NYSERDA Seeding Complete",
        description: `Successfully fed ${data.count} energy saving measures into the NYSERDA Queue.`
      });
      queryClient.invalidateQueries({ queryKey: ["/api/nyserda"] });
    },
    onError: (err: any) => {
      toast({
        title: "Seeding Failed",
        description: err.message,
        variant: "destructive"
      });
    }
  });

  // Save Analysis mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const method = payload.id ? "PUT" : "POST";
      const url = payload.id ? `/api/facility-analyses/${payload.id}` : "/api/facility-analyses";
      const res = await fetch(resolveUrl(url), {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, proposalId })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to save analysis");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${proposalId}/analyses`] });
      setIsOpen(false);
      toast({
        title: "Analysis Saved",
        description: "Successfully updated task energy analysis and metrics."
      });
    },
    onError: (err: any) => {
      toast({
        title: "Save Failed",
        description: err.message,
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await fetch(resolveUrl(`/api/facility-analyses/${id}`), { method: "DELETE" });
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${proposalId}/analyses`] });
      toast({
        title: "Analysis Removed",
        description: "Successfully removed the analysis study from task SOW."
      });
    }
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await fetch(resolveUrl(`/api/facilities/${activeCustomerId}/upload`), {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setAttachments(prev => [...prev, data.fileName]);
      toast({
        title: "File Uploaded",
        description: `Uploaded ${file.name}`
      });
    } catch (err: any) {
      toast({
        title: "Upload Error",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const openCreateModal = (taskId: string) => {
    setSelectedTask(taskId);
    setEditingAnalysis(null);
    setName("");
    setEcmType("boiler");
    setDescription("");
    setProjectCost("0");
    setIncentive("0");
    setElectricSavingsKwh("0");
    setDemandSavingsKw("0");
    setFossilFuelSavingsMmbtu("0");
    setFossilFuelSource("Natural Gas");
    setEnergyCostSavings("0");
    setOperationalSavings("0");
    setIsEcm(true);
    setLinkToDeve(false);
    setImprovementType("ECM");
    setAttachments([]);
    setIsOpen(true);
  };

  const openEditModal = (analysis: any) => {
    setEditingAnalysis(analysis);
    setSelectedTask(analysis.taskId);
    setName(analysis.name);
    setEcmType(analysis.ecmType);
    setDescription(analysis.description || "");
    setProjectCost(String(analysis.projectCost || 0));
    setIncentive(String(analysis.incentive || 0));
    setElectricSavingsKwh(String(analysis.electricSavingsKwh || 0));
    setDemandSavingsKw(String(analysis.demandSavingsKw || 0));
    setFossilFuelSavingsMmbtu(String(analysis.fossilFuelSavingsMmbtu || 0));
    setFossilFuelSource(analysis.fossilFuelSource || "Natural Gas");
    setEnergyCostSavings(String(analysis.energyCostSavings || 0));
    setOperationalSavings(String(analysis.operationalSavings || 0));
    setIsEcm(analysis.isEcm !== false);
    setLinkToDeve(analysis.linkToDeve === true);
    setImprovementType(analysis.improvementType || "ECM");
    setAttachments(analysis.attachments || []);
    setIsOpen(true);
  };

  const handleSave = () => {
    const payload = {
      id: editingAnalysis?.id,
      taskId: selectedTask,
      name,
      ecmType,
      description,
      isEcm,
      linkToDeve,
      improvementType,
      projectCost: parseFloat(projectCost) || 0,
      incentive: parseFloat(incentive) || 0,
      electricSavingsKwh: parseFloat(electricSavingsKwh) || 0,
      demandSavingsKw: parseFloat(demandSavingsKw) || 0,
      fossilFuelSavingsMmbtu: parseFloat(fossilFuelSavingsMmbtu) || 0,
      fossilFuelSource,
      energyCostSavings: parseFloat(energyCostSavings) || 0,
      operationalSavings: parseFloat(operationalSavings) || 0,
      attachments,
      simplePayback: parseFloat(energyCostSavings) > 0 ? (parseFloat(projectCost) / parseFloat(energyCostSavings)) : 0
    };
    saveMutation.mutate(payload);
  };

  if (proposalLoading || analysesLoading) {
    return (
      <AppShell fluid>
        <div className="flex justify-center items-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (!proposal) {
    return (
      <AppShell fluid>
        <div className="text-center py-16">
          <h2 className="text-xl font-bold">Plan Not Found</h2>
          <p className="text-muted-foreground mt-2">The requested proposal or Energy Management Plan does not exist.</p>
        </div>
      </AppShell>
    );
  }

  const generalAnalyses = analysesData?.generalAnalyses || [];
  
  // Calculate aggregate metrics
  const totalCost = generalAnalyses.reduce((acc: number, a: any) => acc + (a.projectCost || 0), 0);
  const totalIncentive = generalAnalyses.reduce((acc: number, a: any) => acc + (a.incentive || 0), 0);
  const totalElectric = generalAnalyses.reduce((acc: number, a: any) => acc + (a.electricSavingsKwh || 0), 0);
  const totalDemand = generalAnalyses.reduce((acc: number, a: any) => acc + (a.demandSavingsKw || 0), 0);
  const totalFuel = generalAnalyses.reduce((acc: number, a: any) => acc + (a.fossilFuelSavingsMmbtu || 0), 0);
  const totalEnergyCost = generalAnalyses.reduce((acc: number, a: any) => acc + (a.energyCostSavings || 0), 0);
  const totalOpexCost = generalAnalyses.reduce((acc: number, a: any) => acc + (a.operationalSavings || 0), 0);
  const totalSavings = totalEnergyCost + totalOpexCost;
  const payback = totalSavings > 0 ? (totalCost - totalIncentive) / totalSavings : 0;

  const standardTasks = [
    { id: "task1", number: "1", title: "Develop energy management plan" },
    { id: "task2", number: "2", title: "Develop energy management plan deliverables" },
    { id: "task3", number: "3", title: "Utility Tracking & Submetering Integration" },
    { id: "task4", number: "4", title: "Conduct walkthrough audits and reports" },
    { id: "task5", number: "5", title: "M&V Tool Development: Case studies" },
    { id: "task6", number: "6", title: "Technical feasibility studies for ECMs" },
    { id: "task7", number: "7", title: "Review O&M schedules and procedures" },
    { id: "task8", number: "8", title: "Training and employee awareness sessions" },
    { id: "task9", number: "9", title: "Scope, support, and financially analyze solar/energy storage" },
    { id: "task10", number: "10", title: "Transition & Final Transition Execution" },
    { id: "task11", number: "11", title: "Reporting: Monthly and Quarterly" }
  ];

  return (
    <AppShell fluid>
      <div className="w-full max-w-[98%] px-4 mx-auto space-y-8 pb-16">
        
        {/* Top Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-green-100 text-green-800 border-green-200">Active EMP</Badge>
              <span className="text-xs text-muted-foreground">Approved on {format(new Date(proposal.createdAt), "MMMM d, yyyy")}</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Energy Management Plan (EMP)</h1>
            <p className="text-muted-foreground">{proposal.companyName} — Living Operational Canvas</p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => setLocation(`/osem-onboarding/${proposalId}`)}
            >
              <FileText className="h-4 w-4 mr-2" /> Modify Proposal Draft
            </Button>
            
            <Button 
              className="bg-primary text-white"
              disabled={seedNyserdaMutation.isPending}
              onClick={() => seedNyserdaMutation.mutate()}
            >
              {seedNyserdaMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Auto-feed NYSERDA Form P
            </Button>
          </div>
        </div>

        {/* Aggregate Metrics Visualizer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase">Annual Electric Savings</p>
                <h3 className="text-2xl font-bold mt-1">{totalElectric.toLocaleString()} kWh</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">Peak Demand: {totalDemand.toFixed(1)} kW</p>
              </div>
              <div className="bg-yellow-50 p-2.5 rounded-full">
                <Zap className="h-5 w-5 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase">Fossil Fuel Savings</p>
                <h3 className="text-2xl font-bold mt-1">{totalFuel.toLocaleString()} MMBtu</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">Natural Gas / Heating Oil</p>
              </div>
              <div className="bg-orange-50 p-2.5 rounded-full">
                <Flame className="h-5 w-5 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase">Total Annual Savings</p>
                <h3 className="text-2xl font-bold mt-1">${totalSavings.toLocaleString()}/yr</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">Energy: ${totalEnergyCost.toLocaleString()} | O&M: ${totalOpexCost.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 p-2.5 rounded-full">
                <Coins className="h-5 w-5 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase">Simple Payback</p>
                <h3 className="text-2xl font-bold mt-1">{payback > 0 ? `${payback.toFixed(1)} years` : "0.0 years"}</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">Net Cost: ${(totalCost - totalIncentive).toLocaleString()}</p>
              </div>
              <div className="bg-blue-50 p-2.5 rounded-full">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Living Plan Narrative */}
        <Card className="border">
          <CardHeader className="bg-muted/10 pb-4 border-b">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Living Plan Documentation Canvas
            </CardTitle>
            <CardDescription>Approved text content of the facility energy management plan.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {proposal.generatedProposal ? (
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap font-serif border-l-4 pl-4 border-primary/20">
                {proposal.generatedProposal}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground text-center py-6">No plan narrative generated. Click "Modify Proposal Draft" to generate.</p>
            )}
          </CardContent>
        </Card>

        {/* Timeline Grid (12/18 Months) */}
        {proposal.projectSchedule && proposal.projectSchedule.length > 0 && (
          <Card className="border">
            <CardHeader className="bg-muted/10 pb-4 border-b">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                EMP Milestone Schedule ({proposal.durationMonths || 12} Months)
              </CardTitle>
              <CardDescription>Gantt chart visualization of planned deliverables across project timeline.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 overflow-x-auto">
              <table className="min-w-full border-collapse border border-border text-xs text-center font-mono">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="p-2 border border-border text-left w-1/3">Task SOW Deliverable</th>
                    {Array.from({ length: proposal.durationMonths || 12 }).map((_, i) => (
                      <th key={i} className="p-2 border border-border w-12">M{i+1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposal.projectSchedule.map((category: any, cIdx: number) => (
                    <tr key={cIdx}>
                      <td className="p-2 border border-border text-left font-semibold bg-muted/10">{category.category || "General tasks"}</td>
                      {Array.from({ length: proposal.durationMonths || 12 }).map((_, mIdx) => {
                        const isScheduled = category.tasks?.some((t: any) => t.months?.includes(mIdx + 1));
                        return (
                          <td key={mIdx} className={`border border-border p-1 ${isScheduled ? "bg-primary/20" : ""}`}>
                            {isScheduled ? "●" : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Task Appendices & SOW Tasks list */}
        <Card className="border">
          <CardHeader className="bg-muted/10 pb-4 border-b">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              SOW Tasks & Energy Studies
            </CardTitle>
            <CardDescription>Manage and link technical feasibility studies, pump metrics, and lighting spreadsheets directly to SOW sections.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-4">
              {standardTasks.map(task => {
                const taskGeneralAnalyses = generalAnalyses.filter((a: any) => a.taskId === task.id);

                // Collect all attachments for this task to compute sequential appendix letters
                const allTaskFiles: string[] = [];
                taskGeneralAnalyses.forEach((a: any) => {
                  if (a.attachments) {
                    a.attachments.forEach((f: string) => {
                      if (!allTaskFiles.includes(f)) {
                        allTaskFiles.push(f);
                      }
                    });
                  }
                });

                return (
                  <div key={task.id} className="border rounded bg-muted/5 overflow-hidden">
                    <div className="flex justify-between items-center p-3 bg-muted/20 border-b">
                      <div>
                        <span className="text-xs font-semibold text-muted-foreground uppercase mr-2">Task {task.number}</span>
                        <h4 className="inline-block text-sm font-semibold">{task.title}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        {task.number === "3" && (
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="link" 
                            className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                            onClick={() => setLocation("/equipment-capture")}
                          >
                            <Laptop className="h-3.5 w-3.5 mr-1" /> Equipment Capture Tool
                          </Button>
                        )}
                        {task.number === "6" && (
                          <>
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="link" 
                              className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                              onClick={() => setLocation("/analyses")}
                            >
                              <Laptop className="h-3.5 w-3.5 mr-1" /> Pump Analysis Tool
                            </Button>
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="link" 
                              className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                              onClick={() => setLocation(`/osem-onboarding/${proposalId}/lighting`)}
                            >
                              <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Lighting Auditor
                            </Button>
                          </>
                        )}
                        <Button type="button" size="sm" variant="outline" onClick={() => openCreateModal(task.id)}>
                          <Plus className="h-4 w-4 mr-1" /> Add Study Appendix
                        </Button>
                      </div>
                    </div>
                    
                    <div className="p-3 space-y-3">
                      {proposal.taskDeliverables?.[task.id] && (
                        <div className="bg-slate-50 p-3 rounded-md border border-slate-200 text-xs">
                          <span className="font-semibold text-muted-foreground block mb-1">Approved Onboarding Task Deliverables:</span>
                          <p className="text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{proposal.taskDeliverables[task.id]}</p>
                        </div>
                      )}

                      {taskGeneralAnalyses.length === 0 ? (
                        <div className="text-xs text-muted-foreground italic">No study folders or energy analyses attached to this task.</div>
                      ) : (
                        taskGeneralAnalyses.map((analysis: any) => (
                          <Card key={analysis.id} className="bg-background shadow-none border">
                            <CardContent className="p-4 space-y-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <Badge variant="outline" className={`mr-2 text-[10px] ${analysis.isEcm !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                                    {analysis.improvementType || "ECM"}
                                  </Badge>
                                  {analysis.linkToDeve && (
                                    <Badge variant="outline" className="mr-2 text-[10px] bg-purple-50 text-purple-700 border-purple-200 font-semibold">DEVE Portfolio</Badge>
                                  )}
                                  <span className="font-semibold text-sm">{analysis.name}</span>
                                  {analysis.description && (
                                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap font-serif border-l-2 pl-2 border-primary/20">{analysis.description}</p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" size="sm" variant="outline" onClick={() => openEditModal(analysis)}>Edit</Button>
                                  <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => deleteMutation.mutate(analysis.id)}>Delete</Button>
                                </div>
                              </div>
                              
                              <div className="border rounded overflow-x-auto mt-2 bg-muted/10">
                                <table className="min-w-full text-xs font-mono">
                                  <thead className="bg-muted/30">
                                    <tr>
                                      <th className="px-2 py-1 text-left">Cost</th>
                                      <th className="px-2 py-1 text-left">Incentive</th>
                                      <th className="px-2 py-1 text-left">Electric Savings (kWh)</th>
                                      <th className="px-2 py-1 text-left">Fuel Savings (MMBtu)</th>
                                      <th className="px-2 py-1 text-left">Energy Cost Savings</th>
                                      <th className="px-2 py-1 text-left text-blue-600">O&M Savings</th>
                                      <th className="px-2 py-1 text-left font-bold text-primary">Payback</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-t">
                                      <td className="px-2 py-1">${(analysis.projectCost || 0).toLocaleString()}</td>
                                      <td className="px-2 py-1">${(analysis.incentive || 0).toLocaleString()}</td>
                                      <td className="px-2 py-1">{(analysis.electricSavingsKwh || 0).toLocaleString()}</td>
                                      <td className="px-2 py-1">{(analysis.fossilFuelSavingsMmbtu || 0).toLocaleString()} ({analysis.fossilFuelSource})</td>
                                      <td className="px-2 py-1">${(analysis.energyCostSavings || 0).toLocaleString()}/yr</td>
                                      <td className="px-2 py-1 text-blue-600">${(analysis.operationalSavings || 0).toLocaleString()}/yr</td>
                                      <td className="px-2 py-1 font-bold text-primary">
                                        {analysis.simplePayback ? `${analysis.simplePayback.toFixed(1)} yrs` : "0.0 yrs"}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              {analysis.attachments && analysis.attachments.length > 0 && (
                                <div className="flex flex-col gap-1.5 mt-2 text-xs">
                                  <span className="font-semibold text-muted-foreground">Study Appendices:</span>
                                  {analysis.attachments.map((file: string, aIdx: number) => {
                                    const fileIdx = allTaskFiles.indexOf(file);
                                    const letter = String.fromCharCode(65 + (fileIdx !== -1 ? fileIdx : aIdx));
                                    return (
                                      <a 
                                        key={aIdx} 
                                        href={`/api/facilities/${activeCustomerId}/download/${file}`}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 text-primary hover:underline font-medium"
                                      >
                                        <Badge variant="outline" className="text-[10px] bg-primary/5 text-primary border-primary/20 font-bold">
                                          Appendix {letter}
                                        </Badge>
                                        <span className="font-mono text-slate-700">{file}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configure Dialog Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingAnalysis ? "Edit Study Appendix" : "Configure SOW Study Appendix"}</DialogTitle>
            <DialogDescription>
              Map energy metrics, specify categories, and upload analysis sheets for this EMP task.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4 text-sm">
            <div className="col-span-2 space-y-1">
              <Label>Analysis Title</Label>
              <Input placeholder="e.g. Pump System VFD Feasibility Study" value={name} onChange={e => setName(e.target.value)} />
            </div>

            <div className="space-y-1">
              <Label>Improvement Category</Label>
              <Select 
                value={improvementType} 
                onValueChange={(val) => {
                  setImprovementType(val);
                  if (val === "ECM") {
                    setIsEcm(true);
                  } else {
                    setIsEcm(false);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ECM">ECM (Energy Conservation Measure)</SelectItem>
                  <SelectItem value="CAPEX">CAPEX (Capital Expenditure)</SelectItem>
                  <SelectItem value="OPEX">OPEX (Operational Expenditure)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col justify-center space-y-2 border p-2 rounded bg-muted/20">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="empIsEcmCheckbox"
                  checked={isEcm} 
                  onChange={e => setIsEcm(e.target.checked)} 
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="empIsEcmCheckbox" className="cursor-pointer font-normal text-xs">This is an ECM (Feeds NYSERDA Appendix P)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="empLinkToDeveCheckbox"
                  checked={linkToDeve} 
                  onChange={e => setLinkToDeve(e.target.checked)} 
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="empLinkToDeveCheckbox" className="cursor-pointer font-normal text-xs text-blue-600 font-semibold">Link to DEVE Portfolio</Label>
              </div>
            </div>
            
            <div className="space-y-1">
              <Label>ECM Type</Label>
              <Select value={ecmType} onValueChange={setEcmType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boiler">Boiler / Circulator</SelectItem>
                  <SelectItem value="rtu">HVAC RTU Replacement</SelectItem>
                  <SelectItem value="bms">BMS & Smart Schedules</SelectItem>
                  <SelectItem value="plug_load">Plug Load Controls</SelectItem>
                  <SelectItem value="solar">Solar PV Analysis</SelectItem>
                  <SelectItem value="lighting">Lighting & Controls</SelectItem>
                  <SelectItem value="pump">Pump Retrofit</SelectItem>
                  <SelectItem value="custom">Other / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label>Fossil Fuel Source</Label>
              <Input value={fossilFuelSource} onChange={e => setFossilFuelSource(e.target.value)} />
            </div>

            <div className="col-span-2 space-y-1">
              <Label>Calculations & Narrative Summary</Label>
              <Textarea 
                placeholder="Include initial observations, measured power data, proposed control strategy..."
                className="min-h-[100px] h-32"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            
            <div className="space-y-1">
              <Label>Estimated Measure Cost ($)</Label>
              <Input type="number" value={projectCost} onChange={e => setProjectCost(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Utility Incentives ($)</Label>
              <Input type="number" value={incentive} onChange={e => setIncentive(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Annual Electric Savings (kWh)</Label>
              <Input type="number" value={electricSavingsKwh} onChange={e => setElectricSavingsKwh(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Summer Peak Demand Savings (kW)</Label>
              <Input type="number" value={demandSavingsKw} onChange={e => setDemandSavingsKw(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Fossil Fuel Savings (MMBtu)</Label>
              <Input type="number" value={fossilFuelSavingsMmbtu} onChange={e => setFossilFuelSavingsMmbtu(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Annual Energy Cost Savings ($/yr)</Label>
              <Input type="number" value={energyCostSavings} onChange={e => setEnergyCostSavings(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label className="text-blue-600">Operational Savings ($/yr)</Label>
              <Input type="number" value={operationalSavings} onChange={e => setOperationalSavings(e.target.value)} />
            </div>

            <div className="col-span-2 space-y-2 border-t pt-4">
              <Label className="text-sm font-semibold">Attached Study Files</Label>
              
              <div className="space-y-2">
                {attachments.map((file, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-muted/30 p-2 rounded border text-xs">
                    <a 
                      href={`/api/facilities/${activeCustomerId}/download/${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80 font-mono"
                    >
                      {file}
                    </a>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 text-destructive hover:bg-destructive/10"
                      onClick={() => setAttachments(prev => prev.filter(f => f !== file))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {attachments.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No files uploaded yet.</p>
                )}
              </div>

              <div className="pt-2">
                <Input 
                  type="file" 
                  onChange={handleFileUpload} 
                  disabled={uploading} 
                  className="text-xs"
                />
                {uploading && (
                  <div className="flex items-center text-xs text-muted-foreground mt-1">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" /> Uploading file...
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button disabled={saveMutation.isPending} onClick={handleSave}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Study & Update EMP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
